import type { FramePresentationEvidence } from '../../../../../libs/PeerCore/src/streamRecovery/contracts';
import { VisiblePresentationRecovery } from '../../../../../libs/PeerCore/src/presentationRecovery/VisiblePresentationRecovery';
import { useEffect, useRef, useState } from 'react';

/** Continuous healthy frames required after connected before hiding cover (5A). */
export const RECONNECT_FRAME_HOLD_MS = 1000;

export interface UseVideoFrameStallOptions {
  /** Ref to the <video> element whose frame progress should be watched. */
  videoRef: React.RefObject<HTMLVideoElement | null>;
  /**
   * Only watch for stalls when we believe an active video stream should be
   * painting frames. When false the watchdog is fully disabled and any raised
   * "stalled" state is cleared, so intentional pause/stop/teardown never trips it.
   */
  enabled: boolean;
  /** No-progress duration (ms) before frames are considered stalled. */
  stallThresholdMs?: number;
  /** How often (ms) to poll for frame progress. */
  pollIntervalMs?: number;
  /**
   * Monotonic count of frames the DECODER has produced, from WebRTC stats.
   *
   * Corroboration for rVFC, which reports frame PRESENTATION through the compositor and goes
   * quiet on Chrome for Android whenever video is promoted to a hardware overlay — while the
   * picture plays perfectly. A decode counter is immune to that: still climbing means frames
   * are arriving, whatever the compositor is doing with them.
   *
   * Returns undefined when no session is reporting yet, which is read as "unknown" and never
   * as "stalled". Omit the probe entirely and behaviour is exactly as before.
   */
  frameProgressProbe?: () => number | undefined;
}

export function useVideoFrameStall({
  videoRef,
  enabled,
  stallThresholdMs = 2_500,
  pollIntervalMs = 150,
  frameProgressProbe,
}: UseVideoFrameStallOptions): boolean {
  const [stalled, setStalled] = useState(false);
  const stalledRef = useRef(false);

  const applyStalled = (next: boolean) => {
    if (next !== stalledRef.current) {
      stalledRef.current = next;
      setStalled(next);
    }
  };

  useEffect(() => {
    if (!enabled) {
      // Never leave a stale cover up when the watchdog is disabled.
      applyStalled(false);
      return;
    }

    const video = videoRef.current;
    if (!video) return;

    const now = () => Date.now();
    let lastProgressTs = now();
    let lastCurrentTime = video.currentTime;
    let disposed = false;
    // Don't report stalls until at least one frame has been painted (rVFC fired)
    // or, for non-rVFC browsers, until currentTime first advances. This prevents
    // false positives during normal WebRTC first-frame decode latency.
    let hasReceivedFirstFrame = false;
    let lastDecodedCount = frameProgressProbe?.();

    const markProgress = () => {
      lastProgressTs = now();
    };

    // rVFC path — fires once per painted frame for high-fidelity progress.
    const rvfcVideo = video as HTMLVideoElement & {
      requestVideoFrameCallback?: (cb: () => void) => number;
      cancelVideoFrameCallback?: (handle: number) => void;
    };
    const supportsRvfc = typeof rvfcVideo.requestVideoFrameCallback === 'function';
    let rvfcHandle: number | undefined;
    const scheduleRvfc = () => {
      if (disposed || !supportsRvfc) return;
      rvfcHandle = rvfcVideo.requestVideoFrameCallback?.(() => {
        hasReceivedFirstFrame = true;
        markProgress();
        scheduleRvfc();
      });
    };
    scheduleRvfc();

    const evaluate = () => {
      // Background tabs throttle rVFC / compositing; treating that as a freeze raises a
      // false reconnect cover. Reset the baseline so returning to the tab does not
      // immediately trip the threshold on stale lastProgressTs.
      if (typeof document !== 'undefined' && document.hidden) {
        lastProgressTs = now();
        applyStalled(false);
        return;
      }

      // Poll-based progress detection — only used as fallback when rVFC is
      // unavailable. When rVFC is supported, it provides high-fidelity frame
      // progress; currentTime on Firefox/Safari advances for live MediaStreams
      // even without frame delivery, which would mask freezes if used alongside rVFC.
      if (!supportsRvfc && video.currentTime !== lastCurrentTime) {
        lastCurrentTime = video.currentTime;
        hasReceivedFirstFrame = true;
        markProgress();
      }

      // Decoder progress corroborates rVFC and outranks it. rVFC can go silent on a
      // perfectly healthy stream — Chrome for Android promotes video to a hardware overlay
      // that bypasses the compositor path rVFC reports on — and with rVFC as the sole signal
      // that silence was indistinguishable from a freeze. A climbing decode counter proves
      // frames are arriving no matter what the compositor is doing with them.
      const decodedNow = frameProgressProbe?.();
      if (decodedNow !== undefined) {
        if (lastDecodedCount !== undefined && decodedNow > lastDecodedCount) {
          hasReceivedFirstFrame = true;
          markProgress();
        }
        lastDecodedCount = decodedNow;
      }

      // Paused/ended playback is intentional — never treat it as a stall.
      // Also, don't report stalls until first frame has been received to avoid
      // false positives during normal WebRTC first-frame decode latency.
      const activelyPlaying = !video.paused && !video.ended;
      const elapsed = now() - lastProgressTs;
      applyStalled(hasReceivedFirstFrame && activelyPlaying && elapsed >= stallThresholdMs);
    };

    const intervalId = setInterval(evaluate, pollIntervalMs);

    return () => {
      disposed = true;
      clearInterval(intervalId);
      if (supportsRvfc && rvfcHandle !== undefined) {
        rvfcVideo.cancelVideoFrameCallback?.(rvfcHandle);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoRef, enabled, stallThresholdMs, pollIntervalMs, frameProgressProbe]);

  return stalled;
}

export interface VideoFrameStallState {
  state: 'healthy' | 'unknown' | 'suspended' | 'stalled';
  episodeId?: string;
  stalledForMs: number;
}

/** Structured recovery uses corroborated session evidence and the original progress clock. */
export function useVideoFrameStallState(options: UseVideoFrameStallOptions & {
  evidenceProbe?: () => FramePresentationEvidence | undefined;
  nowMs?: () => number;
}): VideoFrameStallState {
  const legacy = useVideoFrameStall({ ...options, enabled: options.enabled && !options.evidenceProbe });
  const [state, setState] = useState<VideoFrameStallState>({ state: 'unknown', stalledForMs: 0 });
  const publish = (next: VideoFrameStallState) => setState(previous =>
    previous.state === next.state && previous.episodeId === next.episodeId && previous.stalledForMs === next.stalledForMs ? previous : next);
  const latest = useRef(options);
  latest.current = options;
  const policy = useRef<VisiblePresentationRecovery | null>(null);
  if (!policy.current) policy.current = new VisiblePresentationRecovery();
  const hasProbe = !!options.evidenceProbe;
  useEffect(() => {
    if (!options.enabled || !hasProbe) {
      policy.current?.reset();
      publish({ state: 'suspended', stalledForMs: 0 });
      return;
    }
    const evaluate = () => {
      const evidence = latest.current.evidenceProbe?.();
      if (!evidence) {
        policy.current?.reset();
        publish({ state: 'unknown', stalledForMs: 0 });
        return;
      }
      const decision = policy.current!.observe({ evidence, peerConnected: true,
        nowMs: latest.current.nowMs?.() ?? performance.now() });
      const { state, episodeId, stalledForMs } = decision;
      publish({ state, episodeId, stalledForMs });
    };
    evaluate();
    const handle = setInterval(evaluate, options.pollIntervalMs ?? 150);
    return () => { clearInterval(handle); policy.current?.reset(); };
  }, [options.enabled, options.pollIntervalMs, hasProbe]);
  if (!hasProbe) return { state: legacy ? 'stalled' : options.enabled ? 'healthy' : 'suspended',
    stalledForMs: legacy ? options.stallThresholdMs ?? 2500 : 0 };
  return state;
}

export type ReconnectCoverLatchInput = {
  /** Master enable — when false the latch is forced clear (session left playing). */
  enabled: boolean;
  /** Coordinator / ICE / onRetrying signal. */
  reconnecting: boolean;
  /** Frame-stall watchdog. */
  framesStalled: boolean;
  /** Peer connection is connected. */
  peerConnected: boolean;
  /** True once inbound frames have advanced continuously for the hold window. */
  holdSatisfied: boolean;
  /**
   * Recovery abandoned (terminal disconnect / rejoin) — clear without waiting
   * for frames. Must NOT be true across resetIncomingMedia gaps.
   */
  abandoned?: boolean;
};

/**
 * Pure latch reducer for reconnect cover (decision 5A).
 * Raise on reconnecting or stall; clear only when holdSatisfied while connected,
 * when abandoned, or when disabled. hasVideo false alone never clears.
 */
export function nextReconnectCoverLatched(
  currentlyLatched: boolean,
  input: ReconnectCoverLatchInput,
): boolean {
  if (!input.enabled) return false;
  if (input.reconnecting || input.framesStalled) return true;
  if (!currentlyLatched) return false;
  if (input.abandoned) return false;
  if (input.peerConnected && input.holdSatisfied) return false;
  return true;
}

export interface UseReconnectCoverLatchOptions {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  /** When false, latch is forced clear. */
  enabled: boolean;
  reconnecting: boolean;
  framesStalled: boolean;
  peerConnected: boolean;
  /**
   * Terminal / non-recovery path (disconnected with no retry in flight).
   * Cleared cover without requiring frames.
   */
  abandoned?: boolean;
  holdMs?: number;
  /** Max gap between frame progress samples before the hold window resets. */
  progressGapMs?: number;
  pollIntervalMs?: number;
}

/**
 * Latched reconnect cover: raises on ICE/retry/stall, holds across media clears,
 * and clears only after connected + continuous frames for `holdMs` (or abandoned).
 */
export function useReconnectCoverLatch({
  videoRef,
  enabled,
  reconnecting,
  framesStalled,
  peerConnected,
  abandoned = false,
  holdMs = RECONNECT_FRAME_HOLD_MS,
  progressGapMs = 400,
  pollIntervalMs = 150,
}: UseReconnectCoverLatchOptions): boolean {
  const [latched, setLatched] = useState(false);
  const latchedRef = useRef(false);

  const enabledRef = useRef(enabled);
  const reconnectingRef = useRef(reconnecting);
  const framesStalledRef = useRef(framesStalled);
  const peerConnectedRef = useRef(peerConnected);
  const abandonedRef = useRef(abandoned);
  enabledRef.current = enabled;
  reconnectingRef.current = reconnecting;
  framesStalledRef.current = framesStalled;
  peerConnectedRef.current = peerConnected;
  abandonedRef.current = abandoned;

  const applyLatched = (next: boolean) => {
    if (next !== latchedRef.current) {
      latchedRef.current = next;
      setLatched(next);
    }
  };

  useEffect(() => {
    if (!enabled) {
      applyLatched(false);
      return;
    }

    const video = videoRef.current;
    const now = () => Date.now();
    let lastProgressTs = 0;
    let lastCurrentTime = video?.currentTime ?? 0;
    let healthySince: number | null = null;
    let disposed = false;
    let hasReceivedFrame = false;

    const markProgress = () => {
      lastProgressTs = now();
      hasReceivedFrame = true;
    };

    const rvfcVideo = video as
      | (HTMLVideoElement & {
          requestVideoFrameCallback?: (cb: () => void) => number;
          cancelVideoFrameCallback?: (handle: number) => void;
        })
      | null
      | undefined;
    const supportsRvfc = typeof rvfcVideo?.requestVideoFrameCallback === 'function';
    let rvfcHandle: number | undefined;
    const scheduleRvfc = () => {
      if (disposed || !supportsRvfc || !rvfcVideo) return;
      rvfcHandle = rvfcVideo.requestVideoFrameCallback?.(() => {
        markProgress();
        scheduleRvfc();
      });
    };
    if (video) scheduleRvfc();

    const evaluate = () => {
      if (video && !supportsRvfc && video.currentTime !== lastCurrentTime) {
        lastCurrentTime = video.currentTime;
        markProgress();
      }

      const raise = reconnectingRef.current || framesStalledRef.current;
      const connected = peerConnectedRef.current;
      let holdSatisfied = false;

      if (connected && hasReceivedFrame && lastProgressTs > 0) {
        const gap = now() - lastProgressTs;
        if (gap <= progressGapMs) {
          if (healthySince === null) healthySince = lastProgressTs;
          holdSatisfied = now() - healthySince >= holdMs;
        } else {
          healthySince = null;
        }
      } else {
        healthySince = null;
      }

      if (raise) {
        healthySince = null;
        holdSatisfied = false;
      }

      const next = nextReconnectCoverLatched(latchedRef.current, {
        enabled: enabledRef.current,
        reconnecting: reconnectingRef.current,
        framesStalled: framesStalledRef.current,
        peerConnected: connected,
        holdSatisfied,
        abandoned: abandonedRef.current,
      });
      applyLatched(next);
    };

    // Apply raise/clear immediately when signals change (effect re-runs below too).
    evaluate();
    const intervalId = setInterval(evaluate, pollIntervalMs);

    return () => {
      disposed = true;
      clearInterval(intervalId);
      if (supportsRvfc && rvfcHandle !== undefined) {
        rvfcVideo?.cancelVideoFrameCallback?.(rvfcHandle);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoRef, enabled, holdMs, progressGapMs, pollIntervalMs]);

  // Immediate raise / force-clear on discrete signals (hold-clear stays on the poll).
  useEffect(() => {
    if (!enabled) {
      applyLatched(false);
      return;
    }
    if (reconnecting || framesStalled) {
      applyLatched(true);
      return;
    }
    if (abandoned) {
      applyLatched(false);
    }
  }, [enabled, reconnecting, framesStalled, abandoned]);

  return latched;
}
